import { cibleRelance, expedieLuiMeme, REGLAGES_PAR_DEFAUT } from '@mosaik/core';
import { ErreurApi } from '../api/client';
import {
  useAnnulerLivraison, useConfirmerLivraison,
} from '../api/hooks';
import type { ActionCalculee, CanalEnvoi, CommandeListee, DocumentReglages } from '../api/types';
import { motifArticles } from './CommandeFournisseur';
import { useToasts } from './Toasts';

/**
 * Les fenêtres qu'une action peut avoir besoin d'ouvrir.
 *
 * Elles vivent dans `App`, montées une seule fois : une action déclenchée
 * depuis la liste et la même action déclenchée depuis la fiche doivent ouvrir
 * exactement la même fenêtre.
 */
export interface ModalesCommande {
  onRelanceFournisseur: (c: CommandeListee) => void;
  onRelanceTransporteur: (c: CommandeListee) => void;
  onCommandeARelire: (c: CommandeListee) => void;
  onChoixCommande: (c: CommandeListee, motif: string) => void;
  onArticles: (c: CommandeListee) => void;
  /**
   * Ouvre le mail au client à relire — annonce d'expédition ou numéro de suivi.
   * `colis` désigne un colis supplémentaire ; absent, c'est le colis principal.
   */
  onMailClient: (c: CommandeListee, canal: 'expedition' | 'tracking', colis?: string) => void;
  /** Ouvre l'ajout d'un colis de plus, pour une commande partie en plusieurs envois. */
  onAjoutColis: (c: CommandeListee) => void;
  /** Ouvre la saisie d'une date d'expédition ou d'un tracking, suivie de son mail. */
  onSaisie: (c: CommandeListee, champ: 'expedition' | 'tracking') => void;
  /** Relit le mail déjà envoyé pour un envoi. */
  onVoirMail: (c: CommandeListee, canal: CanalEnvoi) => void;
  /**
   * Demande confirmation avant un geste que le moteur signale — une facture
   * non réglée. `continuer` n'est appelé que si l'utilisateur confirme.
   */
  onFactureNonReglee: (c: CommandeListee, raison: string, continuer: () => void) => void;
  /** Ouvre la fiche — le recours des actions qui se règlent par son formulaire. */
  onOuvrirFiche: (c: CommandeListee) => void;
  /** Ouvre la demande d'enlèvement DHL d'une commande HIOLLE. */
  onEnlevement: (c: CommandeListee) => void;
}

/**
 * Libellé du bouton qui exécute une action. Le même dans la liste et dans la fiche.
 *
 * `reglages` sert à savoir QUI l'on relance : pour une société qui est
 * elle-même le transport, « Écrire au fournisseur » serait trompeur.
 */
export function libelleAction(
  action: ActionCalculee,
  commande: CommandeListee,
  reglages?: DocumentReglages,
): string {
  switch (action.cle) {
    case 'fournisseur':
      return commande.fournisseurs[0]
        ? `Envoyer la commande à ${commande.fournisseurs[0]}`
        : 'Envoyer la commande au fournisseur';
    case 'probleme': return 'Repasser la commande';
    case 'enlevement': return 'Demander l’enlèvement';
    case 'relance': {
      const vers = cibleRelance(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs);
      if (vers === 'transporteur') {
        return commande.transporteur ? `Relancer ${commande.transporteur}` : 'Relancer le transport';
      }
      return commande.fournisseurs[0] ? `Écrire à ${commande.fournisseurs[0]}` : 'Écrire au fournisseur';
    }
    // Jamais « Marquer livrée » : une action à faire ne clôture pas une
    // commande. Ce qu'on fait devant une livraison en retard, c'est réclamer.
    case 'livraison':
      return commande.transporteur
        ? `Relancer ${commande.transporteur}`
        : 'Relancer le transporteur';
    // Jamais « Envoyer » : cocher ne fait rien partir, cela programme un envoi
    // pour trois jours après la livraison. Le libellé promettait un départ
    // immédiat que rien n'a jamais honoré.
    case 'avis': return "Programmer la demande d'avis";
    // Chez une société qui expédie elle-même, il n'y a aucun transporteur à
    // appeler pour obtenir le numéro : « Saisir le tracking » demanderait de
    // taper une information que personne n'a encore communiquée.
    case 'tracking': {
      if (!expedieLuiMeme(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs)) {
        return 'Saisir le tracking';
      }
      return commande.fournisseurs[0]
        ? `Demander le tracking à ${commande.fournisseurs[0]}`
        : 'Demander le tracking au fournisseur';
    }
    // Le bouton nomme le mail qu'il ouvre : c'est désormais vrai.
    case 'mail':
      if (action.colis) return `Envoyer le mail du colis ${action.colis}`;
      return action.canal === 'tracking' ? 'Envoyer le mail de tracking'
        : action.canal === 'expedition' ? 'Envoyer le mail d’expédition'
        : action.canal === 'fournisseur' ? 'Reprendre la commande fournisseur'
        : 'Reprendre l’envoi';
    // L'attente est échue : le geste n'est pas « faire », c'est décider. La
    // fiche porte les deux boutons — reprendre, ou repousser.
    case 'attente': return 'Reprendre ou repousser';
    // Le fournisseur se prévient hors de l'outil ; la fiche porte le bouton
    // qui le constate.
    case 'shopify':
      return commande.fournisseurs[0] ? `Prévenir ${commande.fournisseurs[0]}` : 'Prévenir le fournisseur';
  }
}

/**
 * Ce qu'une action ouvre : une décision PURE, et volontairement à part.
 *
 * Elle vivait dans le `switch` d'un hook React, donc derrière un `useMutation`,
 * un contexte de toasts et un rendu — intestable en pratique, et c'est là que
 * s'était logé le défaut le plus coûteux de l'application : une action
 * « pas de date d'expédition » chez HIOLLE Logistique ouvrait la fenêtre de
 * relance TRANSPORTEUR, qui composait un mail de livraison bloquée sur une
 * commande jamais partie. Deux commandes le faisaient tous les jours.
 *
 * Extraite ici, la règle tient en dix lignes et se vérifie sans navigateur.
 *
 * **La fenêtre suit ce QU'ON RÉCLAME, pas à QUI on l'écrit.** Les deux
 * questions avaient été confondues : `cibleRelance` répond à la seconde —
 * certaines sociétés de l'annuaire SONT le transport, et c'est bien à elles
 * qu'il faut écrire —, mais elle ne dit rien du texte à employer. La fenêtre
 * fournisseur résout ses destinataires par le même annuaire : le bon
 * interlocuteur est donc conservé sans que le message cesse d'être vrai.
 */
export type FenetreAction =
  /** Préparer ou repasser la commande au fournisseur. */
  | 'commande'
  /** Composer, relire puis réserver l'enlèvement DHL chez HIOLLE. */
  | 'enlevement'
  /** Réclamer une date d'expédition — à qui que ce soit. */
  | 'relance-date'
  /** Réclamer le numéro de suivi d'une commande partie, au fournisseur qui l'expédie. */
  | 'relance-tracking'
  /** Réclamer une livraison en retard, muette ou bloquée. */
  | 'relance-livraison'
  /** Le mail d'expédition au client, à relire. */
  | 'mail-expedition'
  /** Le mail de tracking au client, à relire. */
  | 'mail-tracking'
  /** Saisir le numéro de suivi, puis envoyer le mail qui l'annonce. */
  | 'saisie-tracking'
  /** Ce qui se tranche dans la fiche : une attente échue, un mail sans canal connu. */
  | 'fiche'
  /** Ce qui s'exécute d'un clic, sans rien ouvrir. */
  | null;

/**
 * Ce que `fenetreDe` a besoin de savoir de la commande.
 *
 * Facultatif, et le reste : sans contexte, chaque action garde la destination
 * qu'elle avait. Une seule en dépend — `tracking` —, et seulement pour ne pas
 * réclamer un numéro à un transporteur qui n'existe pas.
 */
export interface ContexteFenetre {
  commande: CommandeListee;
  reglages?: DocumentReglages;
  /** Pour une action `mail` : le mail concerné, que porte l'action. */
  canal?: CanalEnvoi;
}

export function fenetreDe(
  cle: ActionCalculee['cle'],
  contexte?: ContexteFenetre,
): FenetreAction {
  switch (cle) {
    // Transmettre et repasser mènent à la même fenêtre : c'est elle qui sait
    // laquelle des deux opérations elle demande, d'après l'incident.
    case 'fournisseur':
    case 'probleme':
      return 'commande';
    case 'enlevement':
      return 'enlevement';
    case 'relance':
      return 'relance-date';
    case 'livraison':
      return 'relance-livraison';
    /*
     * Le tracking se saisit… quand quelqu'un l'a communiqué.
     *
     * Le cas ordinaire est bien un formulaire : le transporteur a rendu son
     * numéro, il reste à le recopier. Mais chez une société qui expédie
     * elle-même — R-TEK fabrique ET expédie —, il n'y a aucun transporteur à
     * qui le demander : le numéro ne viendra que du fournisseur, et tant qu'on
     * ne le lui a pas réclamé, ouvrir un champ vide ne fait rien avancer.
     *
     * Même défaut que la sentinelle HIOLLE, à l'envers : là l'écran écrivait au
     * mauvais interlocuteur, ici il n'offrait d'écrire à personne.
     */
    /*
     * Le tracking se SAISIT… quand quelqu'un l'a communiqué.
     *
     * Le cas ordinaire : le transporteur a rendu son numéro, il reste à le
     * recopier — la fenêtre de saisie s'ouvre, et enchaîne sur le mail qui
     * l'annonce au client. Mais chez une société qui expédie elle-même — R-TEK
     * fabrique ET expédie —, il n'y a aucun transporteur à qui le demander :
     * le numéro ne viendra que du fournisseur, et tant qu'on ne le lui a pas
     * réclamé, ouvrir un champ vide ne fait rien avancer.
     */
    case 'tracking':
      return contexte
        && !contexte.commande.tracking
        && expedieLuiMeme(contexte.reglages ?? REGLAGES_PAR_DEFAUT, contexte.commande.fournisseurs)
        ? 'relance-tracking'
        : 'saisie-tracking';
    /*
     * Un mail à envoyer ou à rejouer ouvre CE mail.
     *
     * Il ouvrait la fiche, faute de savoir lequel : le moteur ne le disait
     * pas. Il le dit désormais (`canal`), et le bouton fait ce qu'il annonce —
     * « Prévenir le client du tracking » prépare le mail de tracking. Sans
     * canal connu, la fiche reste le recours.
     */
    case 'mail':
      if (contexte?.canal === 'fournisseur') return 'commande';
      if (contexte?.canal === 'expedition') return 'mail-expedition';
      if (contexte?.canal === 'tracking') return 'mail-tracking';
      return 'fiche';
    // Une attente échue se tranche sur une date : reprendre, ou repousser. La
    // fiche porte les deux.
    case 'attente':
      return 'fiche';
    // Remboursement ou annulation après transmission : la fiche dit quoi, et
    // porte « Fournisseur prévenu ».
    case 'shopify':
      return 'fiche';
    // La demande d'avis n'ouvre rien et ne part pas : elle se coche. Son geste
    // vit dans `CaseAvis`, et non dans `executer`.
    case 'avis':
      return null;
  }
}

/**
 * Exécute l'action due d'une commande.
 *
 * Extrait de la fiche pour que la liste fasse exactement le même geste : cliquer
 * l'action dans la dernière colonne revient à ouvrir la fiche et à cliquer son
 * bouton, sans le détour. Un seul chemin de code, donc un seul comportement à
 * vérifier — et pas de dérive entre les deux écrans.
 *
 * Deux actions ne s'exécutent pas d'un clic et ouvrent la fiche : saisir un
 * tracking demande un formulaire, et reprendre un mail en échec dépend du canal
 * concerné.
 */
export function useActionCommande(
  reglages: DocumentReglages | undefined,
  modales: ModalesCommande,
  /** Affichage complémentaire de l'erreur, quand l'appelant a un endroit pour ça. */
  onErreur?: (message: string | null) => void,
) {
  const { ajouter } = useToasts();
  const livraison = useConfirmerLivraison();
  const annuler = useAnnulerLivraison();

  const enAttente = livraison.isPending || annuler.isPending;

  function signaler(e: unknown, defaut: string) {
    const message = e instanceof ErreurApi ? e.message : defaut;
    onErreur?.(message);
    ajouter(message, 'bad');
  }

  /**
   * Ouvre la préparation de la commande au fournisseur.
   *
   * Elle n'envoie jamais rien directement : **aucun mail ne part
   * sans avoir été relu**. Ce qui manquait à cette règle, c'était le cas
   * nominal — tout connu, tout résolu —, qui déposait sans montrer. Un mail
   * juste reste un mail qu'on n'a pas vu partir.
   *
   * Deux chemins seulement : le carrefour quand les articles sont à corriger,
   * la fenêtre de relecture sinon. C'est elle qui envoie, avec le texte relu.
   */
  function preparerCommande(commande: CommandeListee) {
    onErreur?.(null);

    // Ce qui manque se répare de deux façons — corriger les articles, ou écrire
    // le mail — et l'utilisateur doit choisir : la première répare la commande
    // pour de bon, la seconde ne règle que cet envoi.
    const motif = motifArticles(commande, reglages);
    if (motif) { modales.onChoixCommande(commande, motif); return; }

    modales.onCommandeARelire(commande);
  }

  async function executer(commande: CommandeListee, action: ActionCalculee): Promise<void> {
    // Où aller est décidé par `fenetreDe`, pure et testée à part. Ne reste ici
    // que l'ouverture elle-même, et le seul geste qui parte sans rien ouvrir.
    switch (fenetreDe(action.cle, { commande, reglages, canal: action.canal })) {
      /*
       * Une facture non réglée ne retire pas la commande de « À faire » : c'est
       * ici, au moment du geste, qu'on demande si l'on veut vraiment avancer
       * l'argent. Confirmer mène au même chemin que les autres commandes.
       */
      case 'commande':
        if (action.blocage) {
          modales.onFactureNonReglee(commande, action.blocage, () => preparerCommande(commande));
          return;
        }
        preparerCommande(commande);
        return;
      // Même fenêtre pour les deux : elle résout ses destinataires par
      // l'annuaire et compose son texte d'après le motif, qu'elle recalcule.
      // Deux fenêtres qui ne différeraient que par un modèle de mail auraient
      // divergé au premier correctif.
      case 'relance-date':
      case 'relance-tracking':
        modales.onRelanceFournisseur(commande);
        return;
      case 'relance-livraison':
        modales.onRelanceTransporteur(commande);
        return;
      case 'enlevement':
        modales.onEnlevement(commande);
        return;
      case 'mail-expedition':
        modales.onMailClient(commande, 'expedition');
        return;
      case 'mail-tracking':
        // Un colis supplémentaire a son propre mail : l'action le nomme.
        modales.onMailClient(commande, 'tracking', action.colis);
        return;
      case 'saisie-tracking':
        modales.onSaisie(commande, 'tracking');
        return;
      case 'fiche':
        modales.onOuvrirFiche(commande);
        return;
      /*
       * `null` ne veut plus dire « pars sans rien ouvrir ».
       *
       * Il n'a plus qu'un cas — la demande d'avis —, et celle-ci ne s'exécute
       * plus : elle se coche. La case porte son propre geste, dans la liste
       * comme dans la fiche, et rien n'arrive donc ici. Le laisser appeler
       * l'API ferait exister deux chemins vers la même écriture, dont un que
       * plus aucun écran ne déclenche.
       */
      case null:
        return;
    }
  }

  /**
   * Clôture une commande.
   *
   * Séparée d'`executer`, et volontairement : clôturer n'est pas « faire ce
   * qu'il reste à faire », c'est déclarer une fin. L'appelant demande
   * confirmation avant — aucun clic ne doit y mener directement.
   */
  async function confirmerLivree(commande: CommandeListee): Promise<void> {
    onErreur?.(null);
    try {
      await livraison.mutateAsync({ id: commande.id, corps: {} });
      ajouter(`${commande.ref} marquée livrée.`);
    } catch (err) { signaler(err, "La livraison n'a pas pu être confirmée."); }
  }

  /**
   * Remet une commande livrée en cours.
   *
   * Séparée d'`executer` : ce n'est pas une action due, c'est la réparation
   * d'un geste précédent. Elle s'offre donc en bas de la fiche, à la place de
   * « Clôturer » dont elle est le symétrique — et nulle part dans la liste,
   * où la colonne « À faire » ne porte que du travail dû.
   */
  async function remettreEnCours(commande: CommandeListee): Promise<void> {
    onErreur?.(null);
    try {
      await annuler.mutateAsync({ id: commande.id });
      ajouter(`${commande.ref} remise en cours.`);
    } catch (err) { signaler(err, "La commande n'a pas pu être remise en cours."); }
  }

  return { executer, confirmerLivree, remettreEnCours, enAttente };
}
